import { endOfMonth, startOfMonth } from 'date-fns';
import { SortingRule } from 'react-table';
import { parseDate, toISODateOnly } from '../../../../../utils/dateUtils';
import {
  ManagerReportsMode,
  mapManagerReportsType,
} from '../../../hooks/useManagerReportsMode';
import {
  AbsencesForManagerFragment,
  AbsenceApprovalStatus,
  AbsenceRequestFilterInput,
  AbsenceRequestSortFieldEnum,
  AbsenceRequestSortInput,
  AscDescEnum,
  AbsenceType,
  Maybe,
} from '../../../__generated__/graphql';
import { mapDocumentFile } from '../../../pages/Absences/mappings';
import { mapApprovalRequests } from '../../../pages/Absences/MyAbsences/mappings';
import { AbsenceRequestFilterType, isAbsenceApprovalStatus } from './types';
import { mapLoomVideo } from '../../../components/LoomVideo';

import { AbsenceRequest } from '../types';

export const mapAbsenceRequests = (
  src: ReadonlyArray<AbsencesForManagerFragment>,
  currentEmployeeId: string,
): AbsenceRequest[] => {
  return src.map<AbsenceRequest>((item) => {
    const isHourly = item.flair__Type__c === AbsenceType.Hourly;
    const isMultiDay = item.flair__Start_Date__c !== item.flair__End_Date__c;

    const relatedApprovalRequest = findRelatedApprovalRequest(
      item.approvalRequests,
      currentEmployeeId,
    );

    const absenceApprovalStatus = item.flair__Approval_Status__c;
    const requestApprovalStatus =
      relatedApprovalRequest?.flair__Approval_Status__c ?? null;

    return {
      id: relatedApprovalRequest?.Id ?? null,
      absenceId: item.Id,
      absenceApprovalStatus: absenceApprovalStatus,
      requestApprovalStatus: requestApprovalStatus,
      categoryName: item.flair__Category_Name__c,
      categoryIcon: item.flair__Category_Icon__c,
      amount: item.flair__Amount__c,
      workingAmount: item.flair__Working_Amount__c,
      startDate: item.flair__Start_Date__c,
      startTime: item.flair__Start_Time__c,
      startDateHalfDay: item.flair__Start_Date_Half_Day__c,
      endDate: item.flair__End_Date__c,
      endTime: item.flair__End_Time__c,
      endDateHalfDay: item.flair__End_Date_Half_Day__c,
      type: item.flair__Type__c,
      requester: {
        id: item.employee.Id,
        name: item.employee.Name,
        avatarUrl: item.employee.avatar?.url,
      },
      approvalRequests: mapApprovalRequests(item.approvalRequests),
      comment: item.flair__Comment__c,
      commentsCount: item.commentsCount,
      createdDate: item.CreatedDate,
      attachments: item.ContentDocumentLinks__r?.map(mapDocumentFile) ?? [],
      recordId: item.Id,
      loomVideo: mapLoomVideo(item.loomVideo),
      isHourly,
      isMultiDay,
      // absence id and extra fields
      extraFields: item.extraFields,
      Id: item.Id,
    };
  });
};

const findRelatedApprovalRequest = (
  src: Maybe<AbsencesForManagerFragment['approvalRequests']>,
  currentEmployeeId: string,
) => {
  if (!src) {
    return null;
  }
  return src.find((item) => item.flair__Approver__c === currentEmployeeId);
};

export const mapSortBy = (
  sortBy: SortingRule<AbsenceRequest>[],
): AbsenceRequestSortInput => {
  if (sortBy.length < 1) {
    return defaultSorting;
  }
  return {
    field: mapSortField(sortBy[0].id),
    order: sortBy[0].desc === true ? AscDescEnum.Desc : AscDescEnum.Asc,
  };
};

const mapSortField = (sortId: string): AbsenceRequestSortFieldEnum => {
  switch (sortId) {
    case '$custom.employee':
      return AbsenceRequestSortFieldEnum.EmployeeName;
    case 'startDate':
      return AbsenceRequestSortFieldEnum.StartDate;
    case 'categoryName':
      return AbsenceRequestSortFieldEnum.CategoryName;
    case 'flair__Working_Amount__c':
      return AbsenceRequestSortFieldEnum.WorkingTime;
    case 'flair__Amount__c':
      return AbsenceRequestSortFieldEnum.CalendarTime;
    case 'flair__Approval_Status__c':
      return AbsenceRequestSortFieldEnum.ApprovalStatus;
    default:
      return AbsenceRequestSortFieldEnum.StartDate;
  }
};

const defaultSorting: AbsenceRequestSortInput = {
  field: AbsenceRequestSortFieldEnum.StartDate,
  order: AscDescEnum.Desc,
};

export const mapAbsenceRequestFilterInput = (
  filter: AbsenceRequestFilterType,
  reportsMode: ManagerReportsMode,
): AbsenceRequestFilterInput => ({
  managerReportsType: mapManagerReportsType(reportsMode),
  startDateFrom: filter.month
    ? toISODateOnly(startOfMonth(parseDate(filter.month)))
    : null,
  startDateTo: filter.month
    ? toISODateOnly(endOfMonth(parseDate(filter.month)))
    : null,
  categoryIds: filter.categoryIds.length > 0 ? filter.categoryIds : null,
  employeeIds: filter.employeeIds.length > 0 ? filter.employeeIds : null,
  approvalStatuses:
    filter.approvalStatuses.length > 0
      ? filter.approvalStatuses.filter((x): x is AbsenceApprovalStatus =>
          isAbsenceApprovalStatus(x),
        )
      : null,
});
